import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Typography,
  Tag,
  Space,
  Button,
  Statistic,
  Row,
  Col,
  theme,
  Select,
  Input,
  Modal,
  Form,
  Alert,
  message,
  Tooltip,
} from "antd";
import {
  ControlOutlined,
  ThunderboltOutlined,
  HomeOutlined,
  RightOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  EditOutlined,
  ApiOutlined,
  WarningOutlined,
} from "@ant-design/icons";

// Custom Battery icon (Ant Design doesn't have one)
const BatteryIcon = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor">
    <path d="M17 4h-3V2h-4v2H7c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 18H7V6h10v16zm-2-10H9v2h6v-2zm0-4H9v2h6V8z" />
  </svg>
);
import PageLayout from "@components/pageLayout/PageLayout.react";
import { authenticatedFetch } from "@/libs/api";
import { makePagination } from "@components/common/TablePagination";

const { Title, Text } = Typography;

/** Format kW/kWh: >= 1000 → MW/MWh with 1 decimal */
function formatPower(val, unit) {
  if (val >= 1000) {
    return { value: (val / 1000).toFixed(1), suffix: unit === "kW" ? "MW" : "MWh" };
  }
  return { value: val.toFixed(2), suffix: unit };
}

// Summary stat card component
function SummaryStatCard({ title, value, suffix, icon, color, loading }) {
  const { token } = theme.useToken();

  return (
    <Card bordered={false} style={{ borderRadius: 12 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 4 }}>{title}</Text>
          <Statistic
            value={loading ? "—" : value}
            suffix={suffix}
            valueStyle={{ fontSize: 28, fontWeight: 700, color: token.colorText }}
            loading={loading}
          />
        </div>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            background: color || "rgba(84, 162, 204, 0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
            color: token.colorPrimary,
          }}
        >
          {icon}
        </div>
      </div>
    </Card>
  );
}

// Batch action bar component
function BatchActionBar({ selectedCount, selectedRows, onChangeGroup, onRemoveFromGroup, onAddToGroup }) {
  const hasIdleSystems = selectedRows.some(r => !r.controlGroupId);
  const hasGroupedSystems = selectedRows.some(r => r.controlGroupId);

  return (
    <div style={{
      background: "#f0f5ff",
      padding: "12px 16px",
      borderRadius: 8,
      marginBottom: 16,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    }}>
      <Text strong>
        <CheckCircleOutlined style={{ marginRight: 8, color: "#1890ff" }} />
        {selectedCount} system(s) selected
      </Text>
      <Space>
        {hasGroupedSystems && (
          <Button onClick={onChangeGroup}>
            Change Control Group
          </Button>
        )}
        {hasGroupedSystems && (
          <Button danger onClick={onRemoveFromGroup}>
            Remove from Group
          </Button>
        )}
        {hasIdleSystems && (
          <Button type="primary" onClick={onAddToGroup}>
            Add to Group
          </Button>
        )}
      </Space>
    </div>
  );
}

export default function ControlGroupSummary() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [systems, setSystems] = useState([]);
  const [controlGroups, setControlGroups] = useState([]);
  const [totals, setTotals] = useState({
    totalGroups: 0,
    totalSystems: 0,
    totalInverterKw: 0,
    totalBatteryKwh: 0,
    runningGroups: 0,
    runningInverterKw: 0,
    runningBatteryKwh: 0,
    idleSystems: 0,
    idleInverterKw: 0,
    idleBatteryKwh: 0,
  });

  const [systemsPageSize, setSystemsPageSize] = useState(10);

  // Selection state
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  // Filter state
  const [filterGroup, setFilterGroup] = useState(null);
  const [searchText, setSearchText] = useState("");

  // Modal state
  const [changeGroupModalOpen, setChangeGroupModalOpen] = useState(false);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [addToGroupModalOpen, setAddToGroupModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Form
  const [changeGroupForm] = Form.useForm();
  const [addToGroupForm] = Form.useForm();
  const [groupForm] = Form.useForm();

  // DUID and create/edit modal state
  const [duids, setDuids] = useState([]);
  const [createEditModalOpen, setCreateEditModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch control groups, systems, and DUIDs in parallel
      const [groupsRes, systemsRes, duidsRes] = await Promise.all([
        authenticatedFetch("/api/control-groups"),
        authenticatedFetch("/api/control-groups/systems"),
        authenticatedFetch("/api/control-groups/duids"),
      ]);

      const groupsData = await groupsRes.json();
      const systemsData = await systemsRes.json();
      const duidsData = await duidsRes.json();

      if (duidsData.success && duidsData.data) {
        setDuids(duidsData.data);
      }

      if (groupsData.success && groupsData.data) {
        setControlGroups(groupsData.data.map(g => ({
          id: g.id,
          name: g.name,
          description: g.description,
          duid: g.duid,
          duidLinkedAt: g.duidLinkedAt,
          // The AEMO market region, from the column of the same name. Distinct
          // from `region` below, which is just a label scraped off the
          // description text and has no bearing on dispatch.
          aemoRegionId: g.aemoRegionId || null,
          timezone: g.timezone || null,
          region: g.description?.split(" - ")[0] || "",
          systemCount: g.systemCount || 0,
          totalInverterKw: g.totalInverterKw || 0,
          totalBatteryKwh: g.totalBatteryKwh || 0,
          status: g.status,
        })));
      }

      if (systemsData.success && systemsData.data) {
        const formattedSystems = systemsData.data.map(s => ({
          id: s.id,
          sn: s.sn,
          nmi: s.nmi,
          controlGroupId: s.controlGroupId,
          controlGroupName: s.controlGroupName,
          status: s.status,
          inverterKW: s.inverterKW,
          batteryKWh: s.batteryKWh,
          customerName: s.customerName,
          state: s.state,
        }));
        setSystems(formattedSystems);

        // Calculate totals from real data
        const groups = groupsData.data || [];
        const totalGroups = groups.length;
        const runningGroups = groups.filter(g => g.status === "ACTIVE" || g.status === "RUNNING").length;
        const totalSystems = formattedSystems.length;
        const totalInverterKw = formattedSystems.reduce((sum, s) => sum + (s.inverterKW || 0), 0);
        const totalBatteryKwh = formattedSystems.reduce((sum, s) => sum + (s.batteryKWh || 0), 0);

        // Idle systems (not assigned to any group)
        const idleSystemsList = formattedSystems.filter(s => !s.controlGroupId);
        const idleSystems = idleSystemsList.length;
        const idleInverterKw = idleSystemsList.reduce((sum, s) => sum + (s.inverterKW || 0), 0);
        const idleBatteryKwh = idleSystemsList.reduce((sum, s) => sum + (s.batteryKWh || 0), 0);

        // Running groups capacity (systems assigned to active groups)
        const assignedSystemsList = formattedSystems.filter(s => s.controlGroupId);
        const runningInverterKw = assignedSystemsList.reduce((sum, s) => sum + (s.inverterKW || 0), 0);
        const runningBatteryKwh = assignedSystemsList.reduce((sum, s) => sum + (s.batteryKWh || 0), 0);

        setTotals({
          totalGroups,
          totalSystems,
          totalInverterKw,
          totalBatteryKwh,
          runningGroups,
          runningInverterKw,
          runningBatteryKwh,
          idleSystems,
          idleInverterKw,
          idleBatteryKwh,
        });
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      message.error("Failed to load control group data");
    } finally {
      setLoading(false);
    }
  };

  // Filter systems
  const filteredSystems = systems.filter(sys => {
    const matchesGroup = !filterGroup || sys.controlGroupId === filterGroup || (filterGroup === "idle" && !sys.controlGroupId);
    const matchesSearch = !searchText ||
      (sys.sn && sys.sn.toLowerCase().includes(searchText.toLowerCase())) ||
      (sys.nmi && sys.nmi.toLowerCase().includes(searchText.toLowerCase())) ||
      (sys.customerName && sys.customerName.toLowerCase().includes(searchText.toLowerCase()));
    return matchesGroup && matchesSearch;
  });

  // Handle change control group
  const handleChangeGroup = async () => {
    try {
      const values = await changeGroupForm.validateFields();
      setActionLoading(true);

      const systemIds = selectedRows.filter(r => r.controlGroupId).map(r => r.id);

      const response = await authenticatedFetch(`/api/control-groups/${values.targetGroupId}/assign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ systemIds }),
      });

      const result = await response.json();

      if (result.success) {
        const targetGroup = controlGroups.find(g => g.id === values.targetGroupId);
        message.success(`Successfully moved ${systemIds.length} system(s) to ${targetGroup?.name}`);
        setChangeGroupModalOpen(false);
        setSelectedRowKeys([]);
        setSelectedRows([]);
        changeGroupForm.resetFields();
        fetchData(); // Refresh data
      } else {
        message.error(result.message || "Failed to change control group");
      }
    } catch (error) {
      console.error("Failed to change group:", error);
      message.error("Failed to change control group");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle remove from group
  const handleRemoveFromGroup = async () => {
    setActionLoading(true);
    try {
      const systemIds = selectedRows.filter(r => r.controlGroupId).map(r => r.id);

      const response = await authenticatedFetch("/api/control-groups/unassign", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ systemIds }),
      });

      const result = await response.json();

      if (result.success) {
        message.success(`Successfully removed ${systemIds.length} system(s) from control group`);
        setRemoveModalOpen(false);
        setSelectedRowKeys([]);
        setSelectedRows([]);
        fetchData(); // Refresh data
      } else {
        message.error(result.message || "Failed to remove from control group");
      }
    } catch (error) {
      console.error("Failed to remove from group:", error);
      message.error("Failed to remove from control group");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle add to group
  const handleAddToGroup = async () => {
    try {
      const values = await addToGroupForm.validateFields();
      setActionLoading(true);

      const systemIds = selectedRows.filter(r => !r.controlGroupId).map(r => r.id);

      const response = await authenticatedFetch(`/api/control-groups/${values.targetGroupId}/assign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ systemIds }),
      });

      const result = await response.json();

      if (result.success) {
        const targetGroup = controlGroups.find(g => g.id === values.targetGroupId);
        message.success(`Successfully added ${systemIds.length} system(s) to ${targetGroup?.name}`);
        setAddToGroupModalOpen(false);
        setSelectedRowKeys([]);
        setSelectedRows([]);
        addToGroupForm.resetFields();
        fetchData(); // Refresh data
      } else {
        message.error(result.message || "Failed to add to control group");
      }
    } catch (error) {
      console.error("Failed to add to group:", error);
    } finally {
      setActionLoading(false);
    }
  };

  // Row selection config
  const rowSelection = {
    type: "checkbox",
    selectedRowKeys,
    onChange: (newSelectedRowKeys, newSelectedRows) => {
      setSelectedRowKeys(newSelectedRowKeys);
      setSelectedRows(newSelectedRows);
    },
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
      {
        key: "active",
        text: "Select Active Only",
        onSelect: () => {
          const activeKeys = filteredSystems
            .filter(s => s.status === "active")
            .map(s => s.id);
          setSelectedRowKeys(activeKeys);
          setSelectedRows(filteredSystems.filter(s => activeKeys.includes(s.id)));
        },
      },
      {
        key: "idle",
        text: "Select Idle Only",
        onSelect: () => {
          const idleKeys = filteredSystems
            .filter(s => !s.controlGroupId)
            .map(s => s.id);
          setSelectedRowKeys(idleKeys);
          setSelectedRows(filteredSystems.filter(s => idleKeys.includes(s.id)));
        },
      },
    ],
  };

  const columns = [
    {
      title: "Serial Number",
      dataIndex: "sn",
      key: "sn",
      fixed: "left",
      width: 150,
      render: (sn) => <Text code style={{ fontSize: 12 }}>{sn}</Text>,
    },
    {
      title: "NMI",
      dataIndex: "nmi",
      key: "nmi",
      width: 140,
      render: (nmi) => <Text type="secondary" style={{ fontSize: 12 }}>{nmi}</Text>,
    },
    {
      title: "Control Group",
      dataIndex: "controlGroupName",
      key: "controlGroupName",
      width: 150,
      render: (name) =>
        name ? (
          <Tag color="blue">{name}</Tag>
        ) : (
          <Tag color="default">— Idle</Tag>
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      filters: [
        { text: "Active", value: "active" },
        { text: "Idle", value: "idle" },
        { text: "Offline", value: "offline" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => {
        const config = {
          active: { color: "success", icon: <CheckCircleOutlined /> },
          idle: { color: "default", icon: <ClockCircleOutlined /> },
          offline: { color: "error", icon: <ExclamationCircleOutlined /> },
        };
        const { color, icon } = config[status] || config.idle;
        return <Tag color={color} icon={icon}>{status}</Tag>;
      },
    },
    {
      title: "Inverter",
      dataIndex: "inverterKW",
      key: "inverterKW",
      width: 100,
      align: "right",
      sorter: (a, b) => a.inverterKW - b.inverterKW,
      render: (val) => (
        <div>
          <Text strong style={{ color: token.colorPrimary }}>{val}</Text>
          <Text type="secondary" style={{ marginLeft: 4, fontSize: 12 }}>kW</Text>
        </div>
      ),
    },
    {
      title: "Battery",
      dataIndex: "batteryKWh",
      key: "batteryKWh",
      width: 100,
      align: "right",
      sorter: (a, b) => a.batteryKWh - b.batteryKWh,
      render: (val) => (
        <div>
          <Text strong style={{ color: token.colorSuccess }}>{val}</Text>
          <Text type="secondary" style={{ marginLeft: 4, fontSize: 12 }}>kWh</Text>
        </div>
      ),
    },
    {
      title: "Customer",
      dataIndex: "customerName",
      key: "customerName",
      width: 150,
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      width: 80,
      align: "center",
      render: (state) => <Tag>{state}</Tag>,
    },
    {
      title: "",
      key: "action",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <Button
          type="link"
          icon={<RightOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/systems/${record.sn}`);
          }}
          style={{ padding: 0 }}
        >
          View
        </Button>
      ),
    },
  ];

  // Group options for Select
  const controlGroupOptions = controlGroups.map(g => ({
    value: g.id,
    label: `${g.name} (${g.region})`,
  }));

  const filterGroupOptions = [
    { value: null, label: "All Groups" },
    { value: "idle", label: "Idle (No Group)" },
    ...controlGroupOptions,
  ];

  // DUID options for select
  // The five NEM regional markets and the zone each is dispatched in.
  // The region decides WHICH region's spot price a PRICE rule compares against —
  // without it the engine cannot look a price up at all and the rule silently
  // never fires. The zone decides when a TIME rule fires; Adelaide runs 30
  // minutes off Sydney, so the wrong zone means the right rule at the wrong
  // moment. Keep this list in step with NEM_REGIONS in controlGroupController.js.
  const NEM_REGION_OPTIONS = [
    { value: "NSW1", label: "NSW1 — New South Wales", tz: "Australia/Sydney" },
    { value: "QLD1", label: "QLD1 — Queensland", tz: "Australia/Brisbane" },
    { value: "SA1", label: "SA1 — South Australia", tz: "Australia/Adelaide" },
    { value: "TAS1", label: "TAS1 — Tasmania", tz: "Australia/Hobart" },
    { value: "VIC1", label: "VIC1 — Victoria", tz: "Australia/Melbourne" },
  ];
  const TIMEZONE_OPTIONS = [
    "Australia/Sydney", "Australia/Brisbane", "Australia/Adelaide",
    "Australia/Hobart", "Australia/Melbourne", "Australia/Perth", "Australia/Darwin",
  ].map(tz => ({ value: tz, label: tz }));

  const duidOptions = duids.map(d => ({
    value: d.duid,
    label: d.isLinked ? `${d.duid} (linked to ${d.linkedTo?.controlGroupName})` : d.duid,
    disabled: d.isLinked && d.linkedTo?.controlGroupId !== editingGroup?.id,
  }));

  // Open create/edit modal
  const openCreateEditModal = (group = null) => {
    setEditingGroup(group);
    if (group) {
      groupForm.setFieldsValue({
        name: group.name,
        description: group.description,
        duid: group.duid || null,
        aemoRegionId: group.aemoRegionId || null,
        timezone: group.timezone || null,
      });
    } else {
      groupForm.resetFields();
    }
    setCreateEditModalOpen(true);
  };

  // Handle create/edit control group
  const handleCreateEditGroup = async () => {
    try {
      const values = await groupForm.validateFields();
      setActionLoading(true);

      const isEdit = !!editingGroup;

      const response = await authenticatedFetch(
        isEdit ? `/api/control-groups/${editingGroup.id}` : "/api/control-groups",
        {
          method: isEdit ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: values.name,
            description: values.description,
            duid: values.duid || null,
            aemoRegionId: values.aemoRegionId || null,
            timezone: values.timezone || null,
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        message.success(isEdit ? "Control group updated successfully" : "Control group created successfully");
        setCreateEditModalOpen(false);
        setEditingGroup(null);
        groupForm.resetFields();
        fetchData();
      } else {
        message.error(result.message || "Failed to save control group");
      }
    } catch (error) {
      console.error("Failed to save control group:", error);
      message.error("Failed to save control group");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <PageLayout active="controlGroupSummary">
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 4 }}>Control Group Management</Title>
        <Text type="secondary" style={{ fontSize: 14 }}>
          Manage control groups and system assignments
        </Text>
      </div>

      {/* Summary Stats - Row 1: Totals */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <SummaryStatCard
            title="Total Groups"
            value={totals.totalGroups}
            icon={<ControlOutlined />}
            color="rgba(84, 162, 204, 0.1)"
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <SummaryStatCard
            title="Total Systems"
            value={totals.totalSystems}
            icon={<HomeOutlined />}
            color="rgba(5, 166, 119, 0.1)"
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <SummaryStatCard
            title="Total Inverter"
            value={formatPower(totals.totalInverterKw, "kW").value}
            suffix={formatPower(totals.totalInverterKw, "kW").suffix}
            icon={<ThunderboltOutlined />}
            color="rgba(250, 173, 20, 0.1)"
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <SummaryStatCard
            title="Total Battery"
            value={formatPower(totals.totalBatteryKwh, "kWh").value}
            suffix={formatPower(totals.totalBatteryKwh, "kWh").suffix}
            icon={<BatteryIcon />}
            color="rgba(245, 166, 35, 0.1)"
            loading={loading}
          />
        </Col>
      </Row>

      {/* Summary Stats - Row 2: Running & Idle */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 4 }}>Running Groups</Text>
                <Text style={{ fontSize: 28, fontWeight: 700 }}>{loading ? "—" : totals.runningGroups}</Text>
              </div>
              <div style={{ display: "flex", gap: 16 }}>
                <div style={{ textAlign: "right" }}>
                  <Text type="secondary" style={{ fontSize: 11, display: "block" }}>Inverter</Text>
                  <Text type="secondary" strong>{formatPower(totals.runningInverterKw, "kW").value} {formatPower(totals.runningInverterKw, "kW").suffix}</Text>
                </div>
                <div style={{ textAlign: "right" }}>
                  <Text type="secondary" style={{ fontSize: 11, display: "block" }}>Battery</Text>
                  <Text type="secondary" strong>{formatPower(totals.runningBatteryKwh, "kWh").value} {formatPower(totals.runningBatteryKwh, "kWh").suffix}</Text>
                </div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 4 }}>Idle Systems</Text>
                <Text style={{ fontSize: 28, fontWeight: 700 }}>{loading ? "—" : totals.idleSystems}</Text>
              </div>
              <div style={{ display: "flex", gap: 16 }}>
                <div style={{ textAlign: "right" }}>
                  <Text type="secondary" style={{ fontSize: 11, display: "block" }}>Inverter</Text>
                  <Text type="secondary" strong>{formatPower(totals.idleInverterKw, "kW").value} {formatPower(totals.idleInverterKw, "kW").suffix}</Text>
                </div>
                <div style={{ textAlign: "right" }}>
                  <Text type="secondary" style={{ fontSize: 11, display: "block" }}>Battery</Text>
                  <Text type="secondary" strong>{formatPower(totals.idleBatteryKwh, "kWh").value} {formatPower(totals.idleBatteryKwh, "kWh").suffix}</Text>
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Control Groups Table */}
      <Card
        bordered={false}
        style={{ borderRadius: 12, marginBottom: 16 }}
        title={
          <Space>
            <ControlOutlined style={{ color: token.colorPrimary }} />
            <span style={{ fontSize: 16, fontWeight: 600 }}>Control Groups</span>
          </Space>
        }
        extra={
          <Space>
            {filterGroup && filterGroup !== "idle" && (
              <Button size="small" onClick={() => setFilterGroup(null)}>
                Clear Filter
              </Button>
            )}
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreateEditModal()}>
              New Control Group
            </Button>
          </Space>
        }
        styles={{
          header: {
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            padding: "12px 16px",
            minHeight: 48,
          },
          body: { padding: "8px 16px" },
        }}
      >
        <Table
          size="small"
          loading={loading}
          dataSource={controlGroups}
          rowKey="id"
          pagination={false}
          onRow={(record) => ({
            onClick: () => setFilterGroup(filterGroup === record.id ? null : record.id),
            style: {
              cursor: "pointer",
              background: filterGroup === record.id ? token.colorPrimaryBg : undefined,
            },
          })}
          columns={[
            {
              title: "Name",
              dataIndex: "name",
              key: "name",
              width: 150,
              render: (name, record) => (
                <Space>
                  <Text strong>{name}</Text>
                  {filterGroup === record.id && <Tag color="blue" style={{ marginLeft: 4 }}>Selected</Tag>}
                </Space>
              ),
            },
            {
              title: "Description",
              dataIndex: "description",
              key: "description",
              ellipsis: true,
              render: (desc) => <Text type="secondary" style={{ fontSize: 12 }}>{desc}</Text>,
            },
            {
              // Surfaced in the list, not just the edit form: groups created
              // before this field existed have it empty, and the only symptom is
              // that their price rules quietly never fire.
              title: "AEMO Region",
              dataIndex: "aemoRegionId",
              key: "aemoRegionId",
              width: 150,
              render: (regionId, record) => regionId ? (
                <Space size={4}>
                  <Tag color="blue">{regionId}</Tag>
                  {!record.timezone && (
                    <Tooltip title="No time zone set — scheduled rules will not evaluate in this group's local time">
                      <WarningOutlined style={{ color: token.colorWarning }} />
                    </Tooltip>
                  )}
                </Space>
              ) : (
                <Tooltip title="Not set — price-based dispatch rules cannot be evaluated for this group. Edit the group to set it.">
                  <Tag color="warning" icon={<WarningOutlined />}>Not set</Tag>
                </Tooltip>
              ),
            },
            {
              title: "DUID",
              dataIndex: "duid",
              key: "duid",
              width: 120,
              render: (duid) => duid ? (
                <Tag color="cyan" icon={<ApiOutlined />}>{duid}</Tag>
              ) : (
                <Text type="secondary" style={{ fontSize: 12 }}>—</Text>
              ),
            },
            {
              title: "Status",
              dataIndex: "status",
              key: "status",
              width: 100,
              align: "center",
              render: (status) => {
                const statusConfig = {
                  ACTIVE: { color: "success", label: "Active" },
                  RUNNING: { color: "processing", label: "Running" },
                  INACTIVE: { color: "default", label: "Inactive" },
                };
                const config = statusConfig[status] || { color: "default", label: status };
                return <Tag color={config.color}>{config.label}</Tag>;
              },
            },
            {
              title: "Systems",
              dataIndex: "systemCount",
              key: "systemCount",
              width: 90,
              align: "center",
              sorter: (a, b) => a.systemCount - b.systemCount,
              render: (count) => <Text strong style={{ color: token.colorPrimary }}>{count}</Text>,
            },
            {
              title: "Inverter (kW)",
              dataIndex: "totalInverterKw",
              key: "totalInverterKw",
              width: 110,
              align: "right",
              sorter: (a, b) => a.totalInverterKw - b.totalInverterKw,
              render: (val) => <Text>{val.toFixed(1)}</Text>,
            },
            {
              title: "Battery (kWh)",
              dataIndex: "totalBatteryKwh",
              key: "totalBatteryKwh",
              width: 110,
              align: "right",
              sorter: (a, b) => a.totalBatteryKwh - b.totalBatteryKwh,
              render: (val) => <Text style={{ color: "#f5a623" }}>{val.toFixed(1)}</Text>,
            },
            {
              title: "",
              key: "action",
              width: 120,
              render: (_, record) => (
                <Space size="small">
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      openCreateEditModal(record);
                    }}
                    size="small"
                  />
                  <Button
                    type="link"
                    icon={<RightOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/control-group/${record.id}`);
                    }}
                    style={{ padding: 0 }}
                  >
                    View
                  </Button>
                </Space>
              ),
            },
          ]}
          locale={{ emptyText: "No control groups found" }}
        />
      </Card>

      {/* Filters and Batch Actions */}
      <Card bordered={false} style={{ borderRadius: 12, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: selectedRowKeys.length > 0 ? 16 : 0 }}>
          <Space>
            <Select
              placeholder="Filter by Group"
              style={{ width: 180 }}
              allowClear
              options={filterGroupOptions}
              value={filterGroup}
              onChange={setFilterGroup}
            />
            <Input
              placeholder="Search by S/N, NMI, or Customer"
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              style={{ width: 280, height: 40 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Space>
          {filterGroup && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Showing: {filterGroup === "idle" ? "Idle Systems" : controlGroups.find(g => g.id === filterGroup)?.name || "All"}
            </Text>
          )}
        </div>

        {selectedRowKeys.length > 0 && (
          <BatchActionBar
            selectedCount={selectedRowKeys.length}
            selectedRows={selectedRows}
            onChangeGroup={() => setChangeGroupModalOpen(true)}
            onRemoveFromGroup={() => setRemoveModalOpen(true)}
            onAddToGroup={() => setAddToGroupModalOpen(true)}
          />
        )}
      </Card>

      {/* Systems Table */}
      <Card
        bordered={false}
        style={{ borderRadius: 12 }}
        title={
          <Space>
            <ControlOutlined style={{ color: token.colorPrimary }} />
            <span style={{ fontSize: 16, fontWeight: 600 }}>All Systems</span>
          </Space>
        }
        extra={
          <Text type="secondary" style={{ fontSize: 13 }}>
            {filteredSystems.length} systems
          </Text>
        }
        styles={{
          header: {
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            padding: "12px 16px",
            minHeight: 48,
          },
          body: { padding: 0 },
        }}
      >
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={filteredSystems}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1100 }}
          pagination={makePagination(systemsPageSize, setSystemsPageSize)}
        />
      </Card>

      {/* Change Control Group Modal */}
      <Modal
        title="Change Control Group"
        open={changeGroupModalOpen}
        onCancel={() => {
          setChangeGroupModalOpen(false);
          changeGroupForm.resetFields();
        }}
        onOk={handleChangeGroup}
        confirmLoading={actionLoading}
        okText="Change Group"
      >
        <Alert
          message={`You are about to change the control group for ${selectedRows.filter(r => r.controlGroupId).length} system(s).`}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={changeGroupForm} layout="vertical">
          <Form.Item
            name="targetGroupId"
            label="Target Control Group"
            rules={[{ required: true, message: "Please select a control group" }]}
          >
            <Select
              placeholder="Select target control group"
              options={controlGroupOptions}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Form>
        <Table
          size="small"
          columns={[
            { title: "S/N", dataIndex: "sn", key: "sn" },
            {
              title: "Current Group",
              dataIndex: "controlGroupName",
              key: "controlGroupName",
              render: (name) => name ? <Tag color="blue">{name}</Tag> : <Tag>— Idle</Tag>
            },
          ]}
          dataSource={selectedRows.filter(r => r.controlGroupId)}
          rowKey="id"
          pagination={false}
          scroll={{ y: 200 }}
        />
      </Modal>

      {/* Remove from Group Modal */}
      <Modal
        title="Remove from Control Group"
        open={removeModalOpen}
        onCancel={() => setRemoveModalOpen(false)}
        onOk={handleRemoveFromGroup}
        confirmLoading={actionLoading}
        okText="Remove"
        okButtonProps={{ danger: true }}
      >
        <Alert
          message={`Are you sure you want to remove ${selectedRows.filter(r => r.controlGroupId).length} system(s) from their control groups?`}
          description="These systems will become idle and won't participate in dispatch until assigned to a new group."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Table
          size="small"
          columns={[
            { title: "S/N", dataIndex: "sn", key: "sn" },
            {
              title: "Current Group",
              dataIndex: "controlGroupName",
              key: "controlGroupName",
              render: (name) => <Tag color="blue">{name}</Tag>
            },
          ]}
          dataSource={selectedRows.filter(r => r.controlGroupId)}
          rowKey="id"
          pagination={false}
          scroll={{ y: 200 }}
        />
      </Modal>

      {/* Add to Group Modal */}
      <Modal
        title="Add Idle Systems to Control Group"
        open={addToGroupModalOpen}
        onCancel={() => {
          setAddToGroupModalOpen(false);
          addToGroupForm.resetFields();
        }}
        onOk={handleAddToGroup}
        confirmLoading={actionLoading}
        okText="Add to Group"
      >
        <Alert
          message={`Adding ${selectedRows.filter(r => !r.controlGroupId).length} idle system(s) to a control group.`}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={addToGroupForm} layout="vertical">
          <Form.Item
            name="targetGroupId"
            label="Target Control Group"
            rules={[{ required: true, message: "Please select a control group" }]}
          >
            <Select
              placeholder="Select control group"
              options={controlGroupOptions}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Form>
        <Table
          size="small"
          columns={[
            { title: "S/N", dataIndex: "sn", key: "sn" },
            { title: "Customer", dataIndex: "customerName", key: "customerName" },
            { title: "State", dataIndex: "state", key: "state" },
          ]}
          dataSource={selectedRows.filter(r => !r.controlGroupId)}
          rowKey="id"
          pagination={false}
          scroll={{ y: 200 }}
        />
      </Modal>

      {/* Create/Edit Control Group Modal */}
      <Modal
        title={editingGroup ? "Edit Control Group" : "Create Control Group"}
        open={createEditModalOpen}
        onCancel={() => {
          setCreateEditModalOpen(false);
          setEditingGroup(null);
          groupForm.resetFields();
        }}
        onOk={handleCreateEditGroup}
        confirmLoading={actionLoading}
        okText={editingGroup ? "Save Changes" : "Create Group"}
        width={480}
      >
        <Form form={groupForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Group Name"
            rules={[{ required: true, message: "Please enter a group name" }]}
          >
            <Input placeholder="e.g., VIC South Region" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea
              placeholder="Describe this control group..."
              rows={3}
            />
          </Form.Item>
          <Form.Item
            name="aemoRegionId"
            label="AEMO Region"
            rules={[{ required: true, message: "Please select the NEM region this group dispatches in" }]}
            tooltip="Which NEM regional market this group sits in. Each region clears its own 5-minute spot price, so price-based dispatch rules cannot be evaluated without it."
            extra="Required for price-based dispatch. Selecting a region fills in the matching time zone."
          >
            <Select
              placeholder="Select region"
              options={NEM_REGION_OPTIONS.map(({ value, label }) => ({ value, label }))}
              style={{ width: "100%" }}
              onChange={(value) => {
                // Fill the zone to match, but leave it editable — a group can
                // legitimately be dispatched on a zone other than its region's.
                const match = NEM_REGION_OPTIONS.find((r) => r.value === value);
                if (match) groupForm.setFieldsValue({ timezone: match.tz });
              }}
            />
          </Form.Item>
          <Form.Item
            name="timezone"
            label="Time Zone"
            rules={[{ required: true, message: "Please select a time zone" }]}
            tooltip="Time zone used to evaluate scheduled dispatch rules. Adelaide runs 30 minutes behind Sydney, so the wrong zone fires the right rule at the wrong moment."
          >
            <Select
              placeholder="Select time zone"
              options={TIMEZONE_OPTIONS}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item
            name="duid"
            label={
              <Space>
                <span>AEMO DUID</span>
                <ApiOutlined style={{ color: token.colorPrimary }} />
              </Space>
            }
            tooltip="Link this control group to an AEMO Dispatchable Unit ID for FCAS bidding"
          >
            <Select
              placeholder="Select DUID (optional)"
              allowClear
              options={duidOptions}
              style={{ width: "100%" }}
            />
          </Form.Item>
          {editingGroup?.duid && (
            <Alert
              message={`Currently linked to ${editingGroup.duid}`}
              description={editingGroup.duidLinkedAt ? `Linked on ${new Date(editingGroup.duidLinkedAt).toLocaleDateString()}` : null}
              type="info"
              showIcon
              icon={<ApiOutlined />}
              style={{ marginBottom: 0 }}
            />
          )}
        </Form>
      </Modal>
    </PageLayout>
  );
}
